/* eslint-disable react/no-array-index-key */
import React, { lazy, Suspense, Fragment } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Switch, Redirect, Route } from 'react-router-dom';
import { useLocation } from 'react-router';
import DashboardLayout from '@cigam/template/dist/layouts/DashboardLayout';
import { LoadingScreen } from '@cigam/template/dist/components';
import useSettings from 'src/hooks/useSettings';
import GuestGuard from 'src/components/GuestGuard';
import AuthRoute from 'src/components/AuthRoute';
import AuthGuard from 'src/components/AuthGuard';
import AuthGuardClient from 'src/components/AuthGuardClient';
import { MODULE } from 'src/config';
import LogService from 'src/services/debug/logService';
import logAxios from 'src/utils/axiosDebug';
import { logout } from 'src/actions/accountActions';

import { THEMES } from 'src/global/constants';
import { navConfig } from 'src/config/navigation';
import { cdnHash } from '@cigam/template/dist/utils';

const routesConfig = [
  {
    exact: true,
    path: '/404',
    component: lazy(() => import('src/views/pages/Error404View')),
  },
  {
    exact: true,
    guard: GuestGuard,
    path: '/configuracao-inicial',
    component: lazy(() => import('src/views/initialConfig')),
  },
  {
    exact: true,
    guard: GuestGuard,
    path: '/login',
    component: lazy(() => import('src/views/auth/LoginView')),
  },
  {
    exact: true,
    guard: GuestGuard,
    path: '/login-cliente',
    component: lazy(() => import('src/views/auth/ClientLoginView')),
  },
  {
    exact: true,
    guard: AuthRoute,
    path: '/',
    component: lazy(() => import('src/views/solutions')),
  },
  // ROTAS CLIENTE
  {
    exact: true,
    layout: DashboardLayout,
    guard: AuthGuardClient,
    path: `${MODULE.cliente.location}`,
    component: () => <Redirect to={`${MODULE.cliente.location}/boletos`} />,
  },
  {
    exact: true,
    layout: DashboardLayout,
    guard: AuthGuardClient,
    path: `${MODULE.cliente.location}/boletos`,
    component: lazy(() => import('src/views/bill')),
  },
  {
    guard: AuthGuard,
    layout: DashboardLayout,
    path: '/',
    routes: [
      // ROTAS SERVICO
      {
        exact: true,
        path: MODULE.servicos.location,
        component: () => <Redirect to={`${MODULE.servicos.location}/kanban`} />,
      },
      {
        exact: true,
        path: `${MODULE.servicos.location}/perfil`,
        component: lazy(() => import('src/views/pages/AccountView')),
      },
      {
        exact: true,
        path: `${MODULE.servicos.location}/dashboard`,
        component: lazy(() => import('src/views/reports/DashboardView')),
      },
      {
        exact: true,
        path: `${MODULE.servicos.location}/kanban`,
        component: lazy(() => import('src/views/kanban/Boards')),
      },
      {
        exact: true,
        path: `${MODULE.servicos.location}/kanban/:sprint`,
        component: lazy(() => import('src/views/kanban/KanbanView')),
      },
      // ROTAS INDUSTRIA
      {
        exact: true,
        path: `${MODULE.industria.location}`,
        component: () => (
          <Redirect to={`${MODULE.industria.location}/dashboard`} />
        ),
      },
      {
        exact: true,
        path: `${MODULE.industria.location}/dashboard`,
        component: lazy(() => import('src/views/reports/DashboardIndustry')),
      },
      {
        exact: true,
        path: `${MODULE.industria.location}/leitor-producao`,
        component: lazy(() => import('src/views/barcodeReader')),
      },
      {
        exact: true,
        path: `${MODULE.industria.location}/leitor-producao`,
        component: lazy(() => import('src/views/barcodeReader')),
      },
      {
        exact: true,
        path: `${MODULE.industria.location}/movimento-producao`,
        component: lazy(() => import('src/views/productionMovement')),
      },
      {
        exact: true,
        path: `${MODULE.industria.location}/test`,
        component: lazy(() => import('src/views/test')),
      },
      {
        component: () => <Redirect to="/404" />,
      },
    ],
  },
  {
    component: () => <Redirect to="/404" />,
  },
];

const renderRoutes = (routes, navigation, user, handleLogout, apmUrl) => {
  return routes ? (
    <Suspense fallback={<LoadingScreen />}>
      <Switch>
        {routes.map((route, i) => {
          const Guard = route.guard || Fragment;
          const Layout = route.layout;
          const Component = route.component;

          return (
            <Route
              key={i}
              path={route.path}
              exact={route.exact}
              render={props => (
                <Guard>
                  {Layout ? (
                    <Layout
                      navConfig={navigation}
                      themes={THEMES}
                      user={user}
                      logService={LogService}
                      logAxios={logAxios}
                      logMonitorUrl={apmUrl}
                      logout={handleLogout}
                    >
                      {route.routes ? (
                        renderRoutes(
                          route.routes,
                          navigation,
                          user,
                          handleLogout
                        )
                      ) : (
                        <Component {...props} />
                      )}
                    </Layout>
                  ) : (
                    <>
                      {route.routes ? (
                        renderRoutes(
                          route.routes,
                          navigation,
                          user,
                          handleLogout
                        )
                      ) : (
                        <Component {...props} />
                      )}
                    </>
                  )}
                </Guard>
              )}
            />
          );
        })}
      </Switch>
    </Suspense>
  ) : null;
};

function chooseMenu(location) {
  const matchNavigation = navConfig.find(mod => {
    const { moduleName, nav } = mod;
    const match = location.pathname.includes(moduleName);

    if (match) {
      return nav;
    }

    return null;
  });

  return matchNavigation || { nav: [] };
}

function Routes() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigation = chooseMenu(location);
  const account = useSelector(state => state.account);
  const { settings } = useSettings();

  const handleLogout = async () => {
    dispatch(logout());
  };

  const apmUrl = () => {
    return `${settings.apmMonitorUrl}/${cdnHash()}`;
  };

  const userName = account?.user?.NomeUsuario || '';

  const user = {
    bio: userName,
    name: userName,
    avatar: null,
  };

  return renderRoutes(routesConfig, navigation, user, handleLogout, apmUrl());
}

export default Routes;
